import type { GridColumn } from './types'

/**
 * The slice of `useUnits` a converting column needs. Narrowed to these three so
 * a caller can memoise them against the measurement system rather than passing
 * the whole hook, whose closures are new on every render.
 */
export interface UnitConverters {
  convertFromMetricV2: (value: number, metricUnit: string) => number | string
  convertToMetricV2: (value: number, metricUnit: string) => number | string
  getImperialUnitLabel: (metricUnit: string) => string
}

/**
 * Swap the unit a header carries for the one the user actually reads in.
 *
 * Several Tolgee strings bake the metric unit into the label — "Expected Flow
 * (l/m)" — so an imperial user would otherwise be told gpm figures are l/m.
 * Only a trailing parenthesised unit is replaced; a header without one is given
 * the unit instead, so the two stay in step if the copy ever changes.
 */
export const withUnitLabel = (headerName: string, unitLabel: string) => {
  if (!unitLabel) return headerName
  return /\([^)]*\)\s*$/.test(headerName)
    ? headerName.replace(/\([^)]*\)\s*$/, `(${unitLabel})`)
    : `${headerName} (${unitLabel})`
}

/**
 * Show a metric-stored quantity in the user's own units.
 *
 * The conversion lives on valueGetter/valueSetter rather than the renderer so
 * that sorting, filtering, the editor's seed value and the displayed cell all
 * agree on one number — and the row itself stays metric, which is what save
 * sends. A renderer-only conversion is the trap this exists to avoid: the cell
 * reads in gpm while the editor opens on the raw l/m, and whatever is typed is
 * written as l/m.
 */
export const unitValueGetter =
  <T,>(field: string, metricUnit: string, units: UnitConverters) =>
  (row: T) => {
    const raw = (row as Record<string, unknown> | undefined)?.[field]
    if (raw == null || raw === '') return raw
    return units.convertFromMetricV2(Number(raw), metricUnit)
  }

/**
 * Convert an edited display value back to metric for storage.
 *
 * Re-entering the number already shown keeps the stored one: display rounds to
 * 2dp, so an unconditional convert-back would drift the value every time an
 * editor was opened (100 l/m → 26.42 gpm → 100.0086 l/m).
 *
 * Grids whose `onCellValueChanged` returns false stage edits themselves and
 * never reach a column's valueSetter (see DataGrid's commit handler), so they
 * call this directly at the point they stage.
 */
export const toMetricValue = (
  stored: unknown,
  edited: unknown,
  metricUnit: string,
  units: UnitConverters,
) => {
  if (edited == null || edited === '') return edited
  const shown =
    stored == null || stored === ''
      ? null
      : units.convertFromMetricV2(Number(stored), metricUnit)
  if (shown != null && Number(shown) === Number(edited)) return stored
  return units.convertToMetricV2(Number(edited), metricUnit)
}

export const applyUnitConversion = <T,>(
  column: GridColumn<T>,
  field: string,
  metricUnit: string,
  units: UnitConverters,
) => {
  const read = (row: T) => (row as Record<string, unknown> | undefined)?.[field]

  column.valueGetter = unitValueGetter<T>(field, metricUnit, units)

  column.valueSetter = (row, value) =>
    ({ [field]: toMetricValue(read(row), value, metricUnit, units) }) as Partial<T>
}
